import { set } from 'lodash';
/**
 * 根据 DataSchema 生成字段的注释
 *
 * @param {DataSchema} schema
 * @param {?ISchemaOption} [option]
 * @returns {*}
 */
function getSchemaComment(schema, option) {
    const { dept = 1, lineComment = true } = option ?? {};
    const { title, description } = schema;
    if (!title)
        return '';
    const indent = dept < 0 ? '' : '  '.repeat(dept);
    if (lineComment)
        return ` // ${title}${(option?.commentWithDescription && description && !description.includes('\n') && (`,${description.replace(/\n/g, '')}`)) || ''}`;
    // 详细注释
    let descLines = [];
    if (description) {
        const splitDesc = description
            .split('\n').filter(line => !!line.trim())
            .map(line => `${indent} * ${line.trim()}`);
        descLines = [
            `${indent} *`,
            ...splitDesc,
        ];
    }
    return [
        `${indent}/**`,
        `${indent} * ${title}`,
        ...descLines,
        `${indent} */`,
    ].join('\n');
}
/**
 * 根据 DataMethodSchema 生成方法参数的类型定义
 *
 * @param {DataSchema} schema
 * @param {Pick<ISchemaOption, 'lineComment'>} option
 * @returns {string}
 */
function getMethodArgumentsType(schema, option) {
    return getDataSchemaType(schema, { ...option, dept: -1, forceFieldsRequired: false });
}
/**
 * 根据 DataMethodSchema 生成方法返回值的类型定义
 *
 * @param {DataSchema} schema
 * @param {Pick<ISchemaOption, 'lineComment'>} option
 * @returns {string}
 */
function getMethodReturnType(schema, option) {
    if (!schema)
        return 'any';
    const { dept = -1 } = option ?? {};
    if (schema?.type === 'object' && (schema?.fields?.length || schema?.methods?.length)) {
        return getDataSchemaType(schema, { ...option, dept, lineComment: true, ignoreObjectId: true })?.trim();
    }
    return getDataSchemaType(schema, { ...option, dept: -1 });
}
;
/**
 * 根据 DataMethodSchema 生成方法字段的类型定义
 *
 * @param {DataMethodSchema} schema
 * @param {?ISchemaOption} [option]
 * @returns {*}
 */
function getMethodSchemaType(schema, option) {
    const { id, arguments: methodArguments = [], isAsync, returnValue } = schema;
    const { dept = 1, lineComment = true } = option ?? {};
    const indent = dept < 0 ? '' : '  '.repeat(dept);
    const returnType = getMethodReturnType(returnValue, { ...option });
    const returnTypeString = isAsync ? `Promise<${returnType}>` : returnType;
    // 对齐参数的 required 属性, 把可选参数后的必选参数改为可选
    const alignMethodArguments = (args) => {
        if (!args.length)
            return args;
        let [preArg] = args;
        args.forEach((arg) => {
            // eslint-disable-next-line no-param-reassign
            if (!preArg.required && arg.required)
                arg.required = false;
            preArg = arg;
        });
        return args;
    };
    const argumentsType = alignMethodArguments(methodArguments)
        .map(argSchema => getMethodArgumentsType(argSchema, { ...option }))
        ?.join(', ') ?? '';
    const typeDefine = `${indent}${id}(${argumentsType}): ${returnTypeString};`;
    const comment = getSchemaComment(schema, { ...option });
    if (!comment)
        return typeDefine;
    if (lineComment)
        return typeDefine.replace(/$/m, comment);
    return [
        comment,
        typeDefine,
    ].join('\n');
}
;
/**
 * 根据 DataMethodSchema 生成类型定义
 *
 * @param {DataMethodSchema} schema
 * @param {?ISchemaOption} [option]
 * @returns {*}
 */
export const getDataSchemaType = (schema, option) => {
    try {
        const { dept = 0, lineComment = true, forceFieldsRequired, ignoreObjectId = false } = option ?? {};
        const nextDept = dept === -1 ? dept : dept + 1;
        const { id, type, required = !!forceFieldsRequired, fields = [], items, methods = [], enum: enumList } = schema;
        if (option && ignoreObjectId === true)
            set(option, 'ignoreObjectId', false);
        const isInline = dept === -1;
        const lf = isInline ? '' : '\n';
        const indent = isInline ? '' : '  '.repeat(dept);
        const delimiter = isInline ? ', ' : lf;
        const semi = isInline ? '' : ';';
        // 获取 {id}?: / ${id}: 的字符串
        const getIdAndColon = (fixRequire = required) => {
            if (!id || ignoreObjectId)
                return '';
            return `${id}${fixRequire ? ':' : '?:'} `;
        };
        const wrapType = (t) => {
            const typeDefine = `${indent}${getIdAndColon()}${t}${semi}`;
            if (isInline)
                return typeDefine;
            const comment = getSchemaComment(schema, { ...option, dept });
            if (!comment)
                return typeDefine;
            if (lineComment)
                return typeDefine.replace(/$/m, comment);
            return [comment, typeDefine].join('\n');
        };
        const wrapObject = (t) => {
            if (isInline || dept === 0)
                return t;
            const comment = getSchemaComment(schema, { ...option, dept });
            if (!comment)
                return t;
            if (lineComment)
                return t.replace(/$/m, comment);
            return [comment, t].join('\n');
        };
        // 枚举
        if (Array.isArray(enumList) && enumList.length) {
            return wrapType(enumList
                .map((item) => {
                try {
                    return JSON.stringify(item.value);
                }
                catch (error) {
                    return null;
                }
            })
                .filter(Boolean)
                .join(' | '));
        }
        switch (type) {
            case 'array': {
                const itemsType = items?.type ?? 'any';
                // 对象数组
                if (itemsType === 'object') {
                    const arrayTypes = items?.fields
                        ?.map(field => getDataSchemaType(field, { ...option, dept: nextDept }))
                        ?.filter(Boolean) ?? [];
                    if (arrayTypes?.length) {
                        return wrapObject([
                            `${indent}${getIdAndColon()}Array<{`,
                            arrayTypes.join(delimiter),
                            `${indent}}>${semi}`,
                        ].join(lf));
                    }
                }
                // 嵌套数组
                if (itemsType === 'array') {
                    return wrapType(`Array<${getDataSchemaType(items, { ...option, dept: -1 })}>`);
                }
                // 普通数组
                return wrapType(`Array<${items?.tsType ?? itemsType}>`);
            }
            case 'object': {
                const start = isInline ? ' ' : lf;
                const end = isInline ? ' ' : indent;
                const fieldTypes = fields
                    .map(field => getDataSchemaType(field, { ...option, dept: nextDept }))
                    .filter(Boolean);
                const methodTypes = methods
                    .map(field => getMethodSchemaType(field, { ...option, dept: nextDept }))
                    .filter(Boolean);
                // 空对象强制转换可选变量
                if (!fieldTypes?.length && !methodTypes?.length) {
                    if (dept === 0) {
                        return wrapObject(`${indent}{}${semi}`);
                    }
                    return wrapObject(`${indent}${getIdAndColon()}{}${semi}`);
                }
                let prompt = dept === 0 ? `{${lf}` : `${indent}${getIdAndColon()}{${start}`;
                if (fieldTypes?.length)
                    prompt += `${fieldTypes.map(f => `${f}`).join(delimiter)}${lf}`;
                if (methodTypes?.length)
                    prompt += `${methodTypes.map(f => `${f}`).join(delimiter)}${lf}`;
                prompt += `${end}}`;
                return wrapObject(prompt);
            }
            case 'string':
            case 'number':
            case 'boolean':
                return wrapType(type);
            case 'integer':
                return wrapType('number');
            case 'any':
            case 'null':
            default:
                return wrapType(schema?.tsType ?? 'any');
        }
    }
    catch (e) {
        console.error(schema, option, e);
        return 'any';
    }
};
